import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Boxes,
  Edit3,
  Image as ImageIcon,
  Package,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';

import { useLoja } from './LojaContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';

const emptyCategory = {
  name: '',
  description: '',
  icon: 'package',
  order: 0,
};

const emptyProduct = {
  name: '',
  description: '',
  price: '',
  category_id: '',
  image_url: '',
  stock: 0,
  active: true,
  metadata: {},
};

export default function LojaAdminCatalog() {
  const { API, authHeaders } = useLoja();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [category, setCategory] = useState(emptyCategory);
  const [product, setProduct] = useState(emptyProduct);

  const [editingCategory, setEditingCategory] = useState('');
  const [editingProduct, setEditingProduct] = useState('');

  const [loading, setLoading] = useState(true);
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadCatalog();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const scrollToSection = () => {
      const id = window.location.hash.replace('#', '');

      if (!id) return;

      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    };

    const timer = setTimeout(scrollToSection, 150);

    window.addEventListener(
      'hashchange',
      scrollToSection
    );

    return () => {
      clearTimeout(timer);

      window.removeEventListener(
        'hashchange',
        scrollToSection
      );
    };
  }, []);

  const showError = err => {
    setMessage('');
    setError(
      err.response?.data?.detail ||
      'Não foi possível concluir a operação.'
    );
  };

  const loadCatalog = async () => {
    setLoading(true);
    setError('');

    try {
      const [catResponse, productResponse] =
        await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/products`),
        ]);

      setCategories(catResponse.data || []);
      setProducts(productResponse.data || []);
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  const saveCategory = async event => {
    event.preventDefault();

    if (!category.name.trim()) {
      setError('Informe o nome da categoria.');
      return;
    }

    setSavingCategory(true);
    setMessage('');
    setError('');

    const payload = {
      name: category.name.trim(),
      description: category.description.trim(),
      icon: category.icon.trim() || 'package',
      order: Number(category.order) || 0,
    };

    try {
      if (editingCategory) {
        await axios.put(
          `${API}/categories/${editingCategory}`,
          payload,
          { headers: authHeaders() }
        );

        setMessage('Categoria atualizada com sucesso.');
      } else {
        await axios.post(
          `${API}/categories`,
          payload,
          { headers: authHeaders() }
        );

        setMessage('Categoria cadastrada com sucesso.');
      }

      setCategory(emptyCategory);
      setEditingCategory('');
      await loadCatalog();
    } catch (err) {
      showError(err);
    } finally {
      setSavingCategory(false);
    }
  };

  const editCategory = item => {
    setEditingCategory(item.id);

    setCategory({
      name: item.name || '',
      description: item.description || '',
      icon: item.icon || 'package',
      order: item.order || 0,
    });

    setMessage('');
    setError('');
  };

  const cancelCategory = () => {
    setEditingCategory('');
    setCategory(emptyCategory);
  };

  const deleteCategory = async item => {
    const confirmed = window.confirm(
      `Desativar a categoria "${item.name}"?`
    );

    if (!confirmed) return;

    setMessage('');
    setError('');

    try {
      await axios.delete(
        `${API}/categories/${item.id}`,
        { headers: authHeaders() }
      );

      if (editingCategory === item.id) {
        cancelCategory();
      }

      setMessage('Categoria desativada.');
      await loadCatalog();
    } catch (err) {
      showError(err);
    }
  };

  const saveProduct = async event => {
    event.preventDefault();

    if (!product.name.trim()) {
      setError('Informe o nome do produto.');
      return;
    }

    const price = Number(
      String(product.price).replace(',', '.')
    );

    if (!Number.isFinite(price) || price < 0) {
      setError('Informe um preço válido.');
      return;
    }

    const stock = Number(product.stock);

    if (!Number.isInteger(stock) || stock < 0) {
      setError('Informe um estoque válido.');
      return;
    }

    setSavingProduct(true);
    setMessage('');
    setError('');

    const payload = {
      name: product.name.trim(),
      description: product.description.trim(),
      price,
      category_id: product.category_id || null,
      image_url: product.image_url.trim(),
      stock,
      active: !!product.active,
      metadata: product.metadata || {},
    };

    try {
      if (editingProduct) {
        await axios.put(
          `${API}/products/${editingProduct}`,
          payload,
          { headers: authHeaders() }
        );

        setMessage('Produto atualizado com sucesso.');
      } else {
        await axios.post(
          `${API}/products`,
          payload,
          { headers: authHeaders() }
        );

        setMessage('Produto cadastrado com sucesso.');
      }

      setProduct(emptyProduct);
      setEditingProduct('');
      await loadCatalog();
    } catch (err) {
      showError(err);
    } finally {
      setSavingProduct(false);
    }
  };

  const editProduct = item => {
    setEditingProduct(item.id);

    setProduct({
      name: item.name || '',
      description: item.description || '',
      price: item.price ?? '',
      category_id: item.category_id || '',
      image_url: item.image_url || '',
      stock: item.stock ?? 0,
      active: item.active !== false,
      metadata: item.metadata || {},
    });

    setMessage('');
    setError('');
  };

  const cancelProduct = () => {
    setEditingProduct('');
    setProduct(emptyProduct);
  };

  const deleteProduct = async item => {
    const confirmed = window.confirm(
      `Desativar o produto "${item.name}"?`
    );

    if (!confirmed) return;

    setMessage('');
    setError('');

    try {
      await axios.delete(
        `${API}/products/${item.id}`,
        { headers: authHeaders() }
      );

      if (editingProduct === item.id) {
        cancelProduct();
      }

      setMessage('Produto desativado.');
      await loadCatalog();
    } catch (err) {
      showError(err);
    }
  };

  const addCustomField = () => {
    const field = {
      id: `field_${Date.now()}`,
      label: '',
      type: 'text',
      required: true,
    };

    setProduct(prev => ({
      ...prev,
      metadata: {
        ...(prev.metadata || {}),
        custom_fields: [
          ...(prev.metadata?.custom_fields || []),
          field,
        ],
      },
    }));
  };

  const updateCustomField = (index, changes) => {
    setProduct(prev => {
      const fields = [
        ...(prev.metadata?.custom_fields || []),
      ];

      fields[index] = {
        ...fields[index],
        ...changes,
      };

      return {
        ...prev,
        metadata: {
          ...(prev.metadata || {}),
          custom_fields: fields,
        },
      };
    });
  };

  const removeCustomField = index => {
    setProduct(prev => ({
      ...prev,
      metadata: {
        ...(prev.metadata || {}),
        custom_fields: (
          prev.metadata?.custom_fields || []
        ).filter((_, fieldIndex) => fieldIndex !== index),
      },
    }));
  };

  const categoryName = id => {
    if (!id) return 'Sem categoria';

    return (
      categories.find(item => item.id === id)?.name ||
      'Categoria não encontrada'
    );
  };

  return (
    <section className="space-y-6">
      <div className="border border-slate-800 bg-slate-950/60 rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-fuchsia-950 flex items-center justify-center">
              <Boxes className="w-5 h-5 text-fuchsia-400" />
            </div>

            <div>
              <h2 className="font-bold text-lg">
                Catálogo
              </h2>

              <p className="text-sm text-slate-400">
                Cadastre categorias e produtos da loja.
              </p>
            </div>
          </div>
        </div>

        {(message || error) && (
          <div className="px-5 pt-5">
            {message && (
              <div className="border border-emerald-700 bg-emerald-950/40 text-emerald-300 rounded-xl p-3 text-sm">
                {message}
              </div>
            )}

            {error && (
              <div className="border border-red-800 bg-red-950/40 text-red-300 rounded-xl p-3 text-sm">
                {error}
              </div>
            )}
          </div>
        )}

        <div className="p-5 grid xl:grid-cols-2 gap-6">
          <div
            id="categorias"
            className="space-y-5 scroll-mt-24"
          >
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-emerald-400" />

                <h3 className="font-bold">
                  Categorias
                </h3>
              </div>

              <form
                onSubmit={saveCategory}
                className="space-y-3"
              >
                <Input
                  placeholder="Nome da categoria"
                  value={category.name}
                  onChange={e =>
                    setCategory(prev => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />

                <Textarea
                  placeholder="Descrição da categoria"
                  value={category.description}
                  onChange={e =>
                    setCategory(prev => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />

                <div className="grid sm:grid-cols-2 gap-3">
                  <Input
                    placeholder="Ícone (ex.: package)"
                    value={category.icon}
                    onChange={e =>
                      setCategory(prev => ({
                        ...prev,
                        icon: e.target.value,
                      }))
                    }
                  />

                  <Input
                    type="number"
                    min="0"
                    placeholder="Ordem"
                    value={category.order}
                    onChange={e =>
                      setCategory(prev => ({
                        ...prev,
                        order: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={savingCategory}
                  >
                    {editingCategory ? (
                      <Save className="w-4 h-4 mr-2" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}

                    {savingCategory
                      ? 'Salvando...'
                      : editingCategory
                        ? 'Salvar categoria'
                        : 'Cadastrar categoria'}
                  </Button>

                  {editingCategory && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelCategory}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancelar
                    </Button>
                  )}
                </div>
              </form>
            </div>

            <div className="space-y-2">
              {loading ? (
                <p className="text-sm text-slate-400">
                  Carregando categorias...
                </p>
              ) : categories.length === 0 ? (
                <p className="text-sm text-slate-500">
                  Nenhuma categoria cadastrada.
                </p>
              ) : (
                categories.map(item => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-3 border border-slate-800 rounded-xl p-3"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold">
                        {item.name}
                      </div>

                      {item.description && (
                        <div className="text-xs text-slate-400 truncate">
                          {item.description}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => editCategory(item)}
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>

                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => deleteCategory(item)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div
            id="produtos"
            className="space-y-5 scroll-mt-24"
          >
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-fuchsia-400" />

                <h3 className="font-bold">
                  Produtos
                </h3>
              </div>

              <form
                onSubmit={saveProduct}
                className="space-y-3"
              >
                <Input
                  placeholder="Nome do produto"
                  value={product.name}
                  onChange={e =>
                    setProduct(prev => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />

                <Textarea
                  placeholder="Descrição do produto"
                  value={product.description}
                  onChange={e =>
                    setProduct(prev => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />

                <div className="grid sm:grid-cols-2 gap-3">
                  <Input
                    placeholder="Preço (ex.: 49,90)"
                    inputMode="decimal"
                    value={product.price}
                    onChange={e =>
                      setProduct(prev => ({
                        ...prev,
                        price: e.target.value,
                      }))
                    }
                  />

                  <Input
                    type="number"
                    min="0"
                    placeholder="Estoque"
                    value={product.stock}
                    onChange={e =>
                      setProduct(prev => ({
                        ...prev,
                        stock: e.target.value,
                      }))
                    }
                  />
                </div>

                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={product.category_id}
                  onChange={e =>
                    setProduct(prev => ({
                      ...prev,
                      category_id: e.target.value,
                    }))
                  }
                >
                  <option value="">
                    Sem categoria
                  </option>

                  {categories.map(item => (
                    <option
                      key={item.id}
                      value={item.id}
                    >
                      {item.name}
                    </option>
                  ))}
                </select>

                <div className="relative">
                  <ImageIcon className="absolute left-3 top-3 w-4 h-4 text-slate-500" />

                  <Input
                    className="pl-9"
                    placeholder="URL da imagem"
                    value={product.image_url}
                    onChange={e =>
                      setProduct(prev => ({
                        ...prev,
                        image_url: e.target.value,
                      }))
                    }
                  />
                </div>

                {product.image_url && (
                  <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-900">
                    <img
                      src={product.image_url}
                      alt="Prévia do produto"
                      className="w-full h-40 object-contain"
                      onError={e => {
                        e.currentTarget.style.display =
                          'none';
                      }}
                    />
                  </div>
                )}

                <div className="border border-slate-800 rounded-xl p-4 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="font-semibold text-sm">
                        Informações solicitadas ao cliente
                      </h4>

                      <p className="text-xs text-slate-400 mt-1">
                        Defina os dados que o cliente deverá
                        informar ao comprar este produto.
                      </p>
                    </div>

                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={addCustomField}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Campo
                    </Button>
                  </div>

                  {(product.metadata?.custom_fields || [])
                    .length === 0 && (
                    <div className="text-xs text-slate-500 border border-dashed border-slate-800 rounded-lg p-3">
                      Nenhuma informação adicional será
                      solicitada neste produto.
                    </div>
                  )}

                  {(product.metadata?.custom_fields || []).map(
                    (field, index) => (
                      <div
                        key={field.id || index}
                        className="border border-slate-800 rounded-lg p-3 space-y-3 bg-slate-950/50"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-400">
                            Campo {index + 1}
                          </span>

                          <button
                            type="button"
                            onClick={() =>
                              removeCustomField(index)
                            }
                            className="text-red-400 hover:text-red-300"
                            title="Remover campo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <Input
                          placeholder="Nome: MAC, Chave, E-mail..."
                          value={field.label || ''}
                          onChange={e =>
                            updateCustomField(index, {
                              label: e.target.value,
                            })
                          }
                        />

                        <div className="grid sm:grid-cols-2 gap-3">
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={field.type || 'text'}
                            onChange={e =>
                              updateCustomField(index, {
                                type: e.target.value,
                              })
                            }
                          >
                            <option value="text">
                              Texto
                            </option>

                            <option value="email">
                              E-mail
                            </option>

                            <option value="password">
                              Senha
                            </option>

                            <option value="url">
                              Link / URL
                            </option>

                            <option value="number">
                              Número
                            </option>

                            <option value="textarea">
                              Texto longo
                            </option>
                          </select>

                          <label className="flex items-center gap-2 h-10 px-3 border border-slate-800 rounded-md text-sm">
                            <input
                              type="checkbox"
                              checked={
                                field.required !== false
                              }
                              onChange={e =>
                                updateCustomField(index, {
                                  required:
                                    e.target.checked,
                                })
                              }
                            />

                            Obrigatório
                          </label>
                        </div>
                      </div>
                    )
                  )}
                </div>

                <label className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={product.active}
                    onChange={e =>
                      setProduct(prev => ({
                        ...prev,
                        active: e.target.checked,
                      }))
                    }
                  />

                  Produto ativo na loja
                </label>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={savingProduct}
                  >
                    {editingProduct ? (
                      <Save className="w-4 h-4 mr-2" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}

                    {savingProduct
                      ? 'Salvando...'
                      : editingProduct
                        ? 'Salvar produto'
                        : 'Cadastrar produto'}
                  </Button>

                  {editingProduct && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelProduct}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancelar
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800 p-5">
          <h3 className="font-bold mb-4">
            Produtos cadastrados
          </h3>

          {loading ? (
            <p className="text-sm text-slate-400">
              Carregando produtos...
            </p>
          ) : products.length === 0 ? (
            <p className="text-sm text-slate-500">
              Nenhum produto cadastrado.
            </p>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
              {products.map(item => (
                <div
                  key={item.id}
                  className="border border-slate-800 rounded-xl overflow-hidden"
                >
                  {item.image_url ? (
                    <div className="h-32 bg-slate-900">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-24 bg-slate-900 flex items-center justify-center">
                      <Package className="w-8 h-8 text-slate-700" />
                    </div>
                  )}

                  <div className="p-3">
                    <div className="font-bold">
                      {item.name}
                    </div>

                    <div className="text-xs text-slate-400 mt-1">
                      {categoryName(item.category_id)}
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      <div>
                        <div className="font-semibold text-emerald-400">
                          {Number(item.price || 0)
                            .toLocaleString(
                              'pt-BR',
                              {
                                style: 'currency',
                                currency: 'BRL',
                              }
                            )}
                        </div>

                        <div className="text-xs text-slate-500">
                          Estoque: {item.stock ?? 0}
                        </div>
                      </div>

                      <div className="flex gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            editProduct(item)
                          }
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>

                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            deleteProduct(item)
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
